import React, { useState } from 'react';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { CustomExpander } from '../../../../components/CustomExpander/CustomExpander';

export interface SalaryDateFiltersProps {
  onSearch: (filters: {
    dateRange: [Date | null, Date | null];
    includeHistoricalMissingPayments: boolean;
  }) => void;
  loading?: boolean;
}

export const SalaryDateFilters: React.FC<SalaryDateFiltersProps> = ({
  onSearch,
  loading = false,
}) => {
  // Calculate default date range
  const getDefaultDateRange = (): [Date, Date] => {
    const now = new Date();

    // Start date: first day of current month
    const startDate = new Date(now.getFullYear(), now.getMonth(), 1);

    // End date: first day of next month
    const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);

    return [startDate, endDate];
  };

  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>(
    getDefaultDateRange(),
  );
  const [
    includeHistoricalMissingPayments,
    setIncludeHistoricalMissingPayments,
  ] = useState<boolean>(false);

  const handleSearch = () => {
    // Validate that both dates are provided
    if (!dateRange[0] || !dateRange[1]) {
      alert('יש לבחור תאריך התחלה וסיום');
      return;
    }

    // Validate that start date is before end date
    if (dateRange[0] > dateRange[1]) {
      alert('תאריך ההתחלה חייב להיות לפני תאריך הסיום');
      return;
    }

    onSearch({
      dateRange,
      includeHistoricalMissingPayments,
    });
  };

  const styles = {
    container: {
      width: '250px',
      maxWidth: '250px',
      minWidth: '250px',
      background: 'white',
      borderRadius: '8px',
      padding: '16px',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      display: 'flex',
      flexDirection: 'column' as const,
    },
    header: {
      fontSize: '1.2rem',
      fontWeight: 'bold',
      marginBottom: '16px',
      color: '#333',
    },
    content: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '8px',
    },
    dateRangeContainer: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '8px',
      padding: '8px',
    },
    calendarWrapper: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '8px',
    },
    calendarLabel: {
      fontSize: '0.9rem',
      color: '#666',
    },
    checkboxContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px',
      direction: 'rtl' as const,
    },
    checkboxLabel: {
      fontSize: '0.95rem',
      color: '#333',
    },
    buttonContainer: {
      marginTop: '16px',
      padding: '8px',
      borderTop: '1px solid #e9ecef',
    },
    searchButton: {
      width: '100%',
      backgroundColor: '#2196F3',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      padding: '12px',
      fontSize: '1rem',
      fontWeight: 'bold',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    },
    searchButtonDisabled: {
      width: '100%',
      backgroundColor: '#9CA3AF',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      padding: '12px',
      fontSize: '1rem',
      fontWeight: 'bold',
      cursor: 'not-allowed',
      transition: 'background-color 0.2s',
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>סינונים</div>
      <div style={styles.content}>
        <CustomExpander>
          <div>טווח תאריכים</div>
          <div style={styles.dateRangeContainer}>
            <div style={styles.calendarWrapper}>
              <span style={styles.calendarLabel}>התחלה:</span>
              <Calendar
                value={dateRange[0]}
                onChange={(e) => setDateRange([e.value as Date, dateRange[1]])}
                dateFormat="dd/mm/yy"
                showIcon
                iconPos="right"
                style={{
                  height: '36px',
                  width: '100%',
                  direction: 'ltr',
                }}
                inputStyle={{ direction: 'rtl' }}
              />
            </div>
            <div style={styles.calendarWrapper}>
              <span style={styles.calendarLabel}>סיום:</span>
              <Calendar
                value={dateRange[1]}
                onChange={(e) => setDateRange([dateRange[0], e.value as Date])}
                dateFormat="dd/mm/yy"
                showIcon
                iconPos="right"
                style={{
                  height: '36px',
                  width: '100%',
                  direction: 'ltr',
                }}
                inputStyle={{ direction: 'rtl' }}
                minDate={dateRange[0] || undefined}
              />
            </div>
          </div>
        </CustomExpander>

        <CustomExpander>
          <div>אפשרויות נוספות</div>
          <div style={styles.checkboxContainer}>
            <Checkbox
              checked={includeHistoricalMissingPayments}
              onChange={(e) =>
                setIncludeHistoricalMissingPayments(e.checked || false)
              }
            />
            <span style={styles.checkboxLabel}>
              כלול תשלומים חסרים היסטוריים
            </span>
          </div>
        </CustomExpander>
      </div>

      <div style={styles.buttonContainer}>
        <Button
          label={loading ? 'מחפש...' : 'חפש'}
          icon={loading ? 'pi pi-spin pi-spinner' : 'pi pi-search'}
          disabled={loading}
          style={loading ? styles.searchButtonDisabled : styles.searchButton}
          onClick={handleSearch}
        />
      </div>
    </div>
  );
};
